import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { dateFmt } from "@/lib/format";
import { ArrowLeft, Loader2, Sparkles, Bug, Wrench, Rocket } from "lucide-react";

const TYPE = {
    feature: { label: "New", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: Sparkles },
    bug: { label: "Fixed", cls: "bg-rose-100 text-rose-800 border-rose-200", icon: Bug },
    improvement: { label: "Improved", cls: "bg-sky-100 text-sky-800 border-sky-200", icon: Wrench },
};

export default function Releases() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/releases").then((r) => setData(r.data)).catch(() => setData({ releases: [] }));
    }, []);

    return (
        <div className="min-h-screen bg-brand-bone">
            <header className="sticky top-0 z-30 backdrop-blur-md bg-white/90 border-b border-border">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
                    <Link to="/"><img src="/procureed-logo.png" alt="ProcureEd" className="h-8 w-auto" /></Link>
                    <Link to="/" className="ml-auto inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-brand-emerald" data-testid="releases-home-link">
                        <ArrowLeft className="h-4 w-4" /> Home
                    </Link>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12" data-testid="releases-page">
                <div className="flex items-center gap-2 text-brand-teal text-sm font-semibold mb-2">
                    <Rocket className="h-4 w-4" /> RELEASE NOTES
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-brand-emerald">What's new in ProcureEd</h1>
                <p className="mt-2 text-muted-foreground">Features and fixes shipped in each release.</p>

                {!data ? (
                    <div className="grid place-items-center py-20 text-brand-emerald"><Loader2 className="h-6 w-6 animate-spin" /></div>
                ) : data.releases.length === 0 ? (
                    <div className="py-20 text-center text-muted-foreground">No release notes yet.</div>
                ) : (
                    <div className="mt-10 space-y-10">
                        {data.releases.map((rel) => (
                            <div key={rel.version} data-testid={`release-${rel.version}`}>
                                <div className="flex flex-wrap items-baseline gap-3 border-b border-border pb-3">
                                    <span className="font-mono text-lg font-bold text-brand-emerald">v{rel.version}</span>
                                    {rel.title && <span className="text-brand-emerald font-medium">{rel.title}</span>}
                                    <span className="ml-auto text-sm text-muted-foreground">{dateFmt(rel.released_at)}</span>
                                </div>
                                <ul className="mt-4 space-y-3">
                                    {rel.items.length === 0 && <li className="text-sm text-muted-foreground">No public items for this release.</li>}
                                    {rel.items.map((it, i) => {
                                        const t = TYPE[it.type] || TYPE.feature;
                                        const Icon = t.icon;
                                        return (
                                            <li key={i} className="flex gap-3" data-testid={`release-item-${rel.version}-${i}`}>
                                                <span className={`shrink-0 inline-flex items-center gap-1 h-6 px-2 rounded-full border text-[11px] font-semibold ${t.cls}`}>
                                                    <Icon className="h-3 w-3" /> {t.label}
                                                </span>
                                                <div>
                                                    <div className="text-sm font-medium text-brand-emerald">{it.title}</div>
                                                    {it.description && <div className="text-sm text-muted-foreground">{it.description}</div>}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
